import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { lcCoachingApi } from '@/api/lcCoaching'
import type { AttendanceRecord } from '@/api/learnerProfile'
import { useAdminLearnerAttendanceHistory } from '@/hooks/admin/useAdminLearnerAttendanceHistory'
import { growthAlertKeys } from '@/hooks/coaching/useGrowthAlertPreview'
import {
  AttendanceHistoryList,
  buildAttendanceHistoryLabels,
} from '@/components/attendance/AttendanceHistoryList'
import { useAttendanceGrowthAlert } from '@/hooks/coaching/useAttendanceGrowthAlert'

interface Props {
  userId: number
}

/** 원생 상세 "수업 기록" 탭 — 출결이력(등하원·결석) 읽기 전용. */
export function StudentSessionHistory({ userId }: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAdminLearnerAttendanceHistory(userId, true)

  const items = data?.pages.flatMap((p) => p.items) ?? []
  const labels = buildAttendanceHistoryLabels(t)

  const { renderActions, dialog } = useAttendanceGrowthAlert(userId)

  if (isLoading) {
    return <p className="py-6 text-center text-sm text-gray-500">{t('common:label.loading')}</p>
  }
  if (isError) {
    return <p className="py-6 text-center text-sm text-gray-600">{t('admin:students.attendanceHistory.error')}</p>
  }
  if (items.length === 0) {
    return <p className="py-6 text-center text-sm text-gray-600">{t('admin:students.attendanceHistory.empty')}</p>
  }

  return (
    <div>
      <AttendanceHistoryList
        items={items}
        labels={labels}
        renderActions={renderActions}
        renderDetails={(record) => (
          <TeacherCommentEditor key={record.attendanceId} userId={userId} record={record} />
        )}
      />
      {hasNextPage && (
        <button
          type="button"
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
          className="mt-4 w-full rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-600"
        >
          {t('admin:students.attendanceHistory.loadMore')}
        </button>
      )}
      {dialog}
    </div>
  )
}

function TeacherCommentEditor({ userId, record }: { userId: number; record: AttendanceRecord }) {
  const { t } = useTranslation(['admin', 'common'])
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [savedComment, setSavedComment] = useState(record.coachingNote ?? '')
  const [savedVersion, setSavedVersion] = useState(record.coachingNoteVersion)
  const [draft, setDraft] = useState(savedComment)
  const attendanceId = record.attendanceId
  const canEdit = record.status === 'PRESENT' && attendanceId != null
  const update = useMutation({
    mutationFn: (note: string | null) =>
      lcCoachingApi.updateAttendanceNote(userId, attendanceId as number, note, savedVersion),
    onSuccess: async (data) => {
      setSavedComment(data.note ?? '')
      setDraft(data.note ?? '')
      setSavedVersion(data.version)
      setEditing(false)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['lc', 'admin', 'learner', userId, 'attendance', 'history'] }),
        queryClient.invalidateQueries({ queryKey: growthAlertKeys.preview(userId, record.date) }),
      ])
    },
  })

  if (!canEdit) return null

  if (editing) {
    return (
      <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
        <label className="block text-xs font-semibold text-gray-700">
          {t('admin:students.attendanceHistory.teacherComment')}
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={3}
            placeholder={t('admin:students.attendanceHistory.teacherCommentPlaceholder')}
            className="mt-1.5 w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </label>
        {update.isError ? (
          <p className="mt-1 text-xs text-red-600" role="alert">
            {t('admin:students.attendanceHistory.teacherCommentSaveError')}
          </p>
        ) : null}
        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setDraft(savedComment)
              setEditing(false)
            }}
            disabled={update.isPending}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-white disabled:opacity-50"
          >
            {t('admin:students.attendanceHistory.teacherCommentCancel')}
          </button>
          <button
            type="button"
            onClick={() => update.mutate(draft.trim() || null)}
            disabled={update.isPending}
            className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {t('admin:students.attendanceHistory.teacherCommentSave')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-2 flex items-start justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2">
      <div className="min-w-0">
        <p className="text-xs font-semibold text-gray-600">
          {t('admin:students.attendanceHistory.teacherComment')}
        </p>
        <p className="mt-0.5 whitespace-pre-wrap text-sm text-gray-800">
          {savedComment || t('admin:students.attendanceHistory.teacherCommentEmpty')}
        </p>
      </div>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="shrink-0 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100"
      >
        {savedComment
          ? t('admin:students.attendanceHistory.teacherCommentEdit')
          : t('admin:students.attendanceHistory.teacherCommentAdd')}
      </button>
    </div>
  )
}
